import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const DOC_MAP = {
  privacy: { key: "privacy_policy", title: "Privacy Policy" },
  terms: { key: "terms_of_service", title: "Terms of Service" },
};

export default function Legal() {
  const { docSlug } = useParams();
  const cfg = DOC_MAP[docSlug];
  const { data, loading } = useSiteSettings();

  const content = useMemo(() => {
    if (!cfg) return "";
    return (data?.[cfg.key] || "").trim();
  }, [cfg, data]);

  if (!cfg) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
      <header className="sticky top-0 z-30 glass border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-brand-link">
            <Logo className="h-9" />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="legal-back-home"
              className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 hover:dark:text-white transition-colors inline-flex items-center gap-1.5"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to site
            </Link>
            <ThemeToggle size="sm" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-500 dark:text-cyan-400 mb-3">
          / Legal
        </div>
        <h1
          data-testid="legal-page-title"
          className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mb-3"
        >
          {cfg.title}
        </h1>
        {data?.updated_at && (
          <div className="text-xs text-slate-500 font-mono-tech mb-10">
            Last updated · {new Date(data.updated_at).toLocaleDateString()}
          </div>
        )}

        <article
          data-testid="legal-page-content"
          className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-display prose-headings:tracking-tight prose-a:text-cyan-600 dark:prose-a:text-cyan-400 prose-strong:text-slate-900 dark:prose-strong:text-white"
        >
          {loading && !content ? (
            <p className="text-slate-500">Loading…</p>
          ) : (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
          )}
        </article>

        <div className="mt-16 pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row gap-3 text-sm">
          <Link
            to="/legal/privacy"
            className="text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white"
            data-testid="legal-link-privacy"
          >
            Privacy Policy
          </Link>
          <span className="text-slate-300 dark:text-slate-700 hidden sm:inline">·</span>
          <Link
            to="/legal/terms"
            className="text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white"
            data-testid="legal-link-terms"
          >
            Terms of Service
          </Link>
        </div>
      </main>
    </div>
  );
}
